import { getDbSafe, getAuthSafe } from './firebaseConfig';
import { doc, getDoc, setDoc, updateDoc, collection, query, where, getDocs, serverTimestamp } from 'firebase/firestore';

/**
 * Ban appeals.
 *
 * The appeal document id is the appellant's uid, deliberately: a Firestore rule
 * cannot query a collection, so "does this user already have a pending appeal?"
 * is only answerable in rules when the id is derivable from the caller. One
 * document per user, carrying the current ban cycle's appeal.
 *
 * Every limit enforced here is ALSO enforced in firestore.rules. These checks
 * exist to produce a decent error message; the rules are the actual control.
 */

export const APPEAL_MAX_LENGTH = 1000;
/** How long after a denial before the account may appeal again. */
export const APPEAL_COOLDOWN_DAYS = 30;

export type AppealStatus = 'pending' | 'approved' | 'denied';

export interface BanAppeal {
  userId: string;
  appealText: string;
  status: AppealStatus;
  submittedAt?: any;
  /** Copied from the user doc at submit time so it survives a Watcher editing or clearing it. */
  bannedAt?: any;
  banReason?: string | null;
  deniedAt?: any;
  decidedBy?: string;
  /** Short note from the Watcher, shown back to the appellant on a denial. */
  watcherNote?: string;
}

const toMs = (v: any): number | null => {
  if (v == null) return null;
  if (typeof v?.toMillis === 'function') return v.toMillis();
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.getTime();
};

/** When this appeal may be resubmitted, or null if it may be right now. */
export function cooldownEndsAt(appeal: BanAppeal | null): Date | null {
  if (!appeal || appeal.status !== 'denied') return null;
  const denied = toMs(appeal.deniedAt);
  if (denied == null) return null;
  const ends = denied + APPEAL_COOLDOWN_DAYS * 24 * 60 * 60 * 1000;
  return ends > Date.now() ? new Date(ends) : null;
}

export const BanAppealService = {
  /** This user's appeal for the current ban, or null if they have never filed one. */
  async getMyAppeal(userId: string): Promise<BanAppeal | null> {
    try {
      const db = await getDbSafe();
      const snap = await getDoc(doc(db, 'ban_appeals', userId));
      return snap.exists() ? ({ ...(snap.data() as BanAppeal) }) : null;
    } catch (e) {
      console.warn('[BanAppealService] getMyAppeal failed:', e);
      return null;
    }
  },

  /**
   * File or re-file an appeal.
   *
   * A first appeal is a create; a re-appeal after a served cooldown is an update,
   * because the previous decision has to stay on file — deleting it to re-create
   * would erase the very deniedAt the cooldown is measured from.
   */
  async submitAppeal(userId: string, appealText: string): Promise<{ success: boolean; error?: string }> {
    const text = (appealText || '').trim();
    if (!text) return { success: false, error: 'Write a short explanation first.' };
    if (text.length > APPEAL_MAX_LENGTH) {
      return { success: false, error: `Keep it under ${APPEAL_MAX_LENGTH} characters.` };
    }

    try {
      const db = await getDbSafe();
      const auth = await getAuthSafe();
      if (!auth.currentUser || auth.currentUser.uid !== userId) {
        return { success: false, error: 'Not signed in.' };
      }

      const existing = await BanAppealService.getMyAppeal(userId);
      if (existing?.status === 'pending') {
        return { success: false, error: 'Your appeal is already being reviewed.' };
      }
      if (existing?.status === 'approved') {
        return { success: false, error: 'This appeal was already approved.' };
      }
      const waitUntil = cooldownEndsAt(existing);
      if (waitUntil) {
        return {
          success: false,
          error: `You can appeal again after ${waitUntil.toLocaleDateString()}.`,
        };
      }

      // Copied from the user doc now so the appeal still says what it was
      // contesting even if a Watcher later edits or clears the ban fields.
      const userSnap = await getDoc(doc(db, 'users', userId));
      const u = userSnap.exists() ? userSnap.data() : {};

      const payload: any = {
        userId,
        appealText: text,
        status: 'pending',
        submittedAt: serverTimestamp(),
        bannedAt: u.bannedAt || null,
        banReason: u.banReason || null,
      };

      if (existing) {
        // Re-appeal: the narrow update path. deniedAt/decidedBy/watcherNote are
        // left in place — the rule's affectedKeys check requires exactly that.
        await updateDoc(doc(db, 'ban_appeals', userId), payload);
      } else {
        await setDoc(doc(db, 'ban_appeals', userId), payload);
      }
      return { success: true };
    } catch (e: any) {
      console.error('[BanAppealService] submitAppeal failed:', e);
      // A rules rejection here means the client-side checks above and the rules
      // disagree; say something honest rather than surfacing a raw error.
      return { success: false, error: 'Could not submit your appeal right now.' };
    }
  },

  // ── Watcher side ─────────────────────────────────────────────────────────────

  /** Appeals awaiting a decision, for the moderation queue. */
  async fetchAppeals(status: AppealStatus = 'pending'): Promise<Array<BanAppeal & { id: string }>> {
    try {
      const db = await getDbSafe();
      const snap = await getDocs(query(collection(db, 'ban_appeals'), where('status', '==', status)));
      return snap.docs.map(d => ({ id: d.id, ...(d.data() as BanAppeal) }));
    } catch (e) {
      console.error('[BanAppealService] fetchAppeals failed:', e);
      return [];
    }
  },

  /**
   * Approve: lift the ban and record it.
   *
   * The ban is lifted through LegionService.unbanUser rather than by clearing
   * the fields here, so there is one unban path rather than two that can drift.
   * Because the client watches its own profile document live, the account
   * regains access without relaunching.
   *
   * Does NOT touch banned_fingerprints: a reversed ban on this account says
   * nothing about whether a fingerprint match against a different signup was
   * right. Separate reversals.
   */
  async approveAppeal(userId: string, watcherId: string, note?: string): Promise<{ success: boolean; error?: string }> {
    try {
      const db = await getDbSafe();
      const { LegionService } = await import('./api');
      const res = await LegionService.unbanUser(userId, note || 'Ban appeal approved');
      if (!res.success) return res;

      await updateDoc(doc(db, 'ban_appeals', userId), {
        status: 'approved',
        decidedBy: watcherId,
        decidedAt: serverTimestamp(),
        watcherNote: note || null,
      });

      const { logAuditAction } = await import('./WatcherService');
      const actor = await getDoc(doc(db, 'users', watcherId));
      await logAuditAction({
        watcher_id: watcherId,
        watcher_username: actor.exists() ? (actor.data().username || 'Unknown') : 'Unknown',
        target_user_id: userId,
        action_type: 'appeal_approved',
        reason: note || 'Ban appeal approved',
      } as any);

      return { success: true };
    } catch (e: any) {
      console.error('[BanAppealService] approveAppeal failed:', e);
      return { success: false, error: e.message || 'Could not approve the appeal' };
    }
  },

  /**
   * Deny: the ban stands. deniedAt starts the cooldown that the rules enforce,
   * and the note is shown back to the appellant so a denial does not read as the
   * form having silently done nothing.
   */
  async denyAppeal(userId: string, watcherId: string, note?: string): Promise<{ success: boolean; error?: string }> {
    try {
      const db = await getDbSafe();
      await updateDoc(doc(db, 'ban_appeals', userId), {
        status: 'denied',
        deniedAt: serverTimestamp(),
        decidedBy: watcherId,
        watcherNote: note || null,
      });

      const { logAuditAction } = await import('./WatcherService');
      const actor = await getDoc(doc(db, 'users', watcherId));
      await logAuditAction({
        watcher_id: watcherId,
        watcher_username: actor.exists() ? (actor.data().username || 'Unknown') : 'Unknown',
        target_user_id: userId,
        action_type: 'appeal_denied',
        reason: note || 'Ban appeal denied',
      } as any);

      return { success: true };
    } catch (e: any) {
      console.error('[BanAppealService] denyAppeal failed:', e);
      return { success: false, error: e.message || 'Could not deny the appeal' };
    }
  },
};
